import React, { useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Chip,
  Card,
  CardBody,
  Accordion,
  AccordionItem,
  Progress,
} from "@heroui/react";
import {
  Plus,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import {
  DocumentCategory,
  RequiredContext,
  CreateDocumentTemplateDto,
  DocxAnalysisResult,
  ExtractedPlaceholder,
  EntityInfo,
  UploadAnalysisResult,
} from "./types";

interface CreateDocumentTemplateModalEntityBasedProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateDocumentTemplateModalEntityBased: React.FC<
  CreateDocumentTemplateModalEntityBasedProps
> = ({ isOpen, onClose, onSuccess }) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [analysisResult, setAnalysisResult] =
    useState<UploadAnalysisResult | null>(null);
  const [previewAnalysis, setPreviewAnalysis] =
    useState<DocxAnalysisResult | null>(null);
  const [entityInfo, setEntityInfo] = useState<Record<string, EntityInfo>>({});

  // Form data
  const [formData, setFormData] = useState<CreateDocumentTemplateDto>({
    templateKey: "",
    name: "",
    category: DocumentCategory.GENERAL,
    sharePointTemplateId: "",
    description: "",
    availablePlaceholders: [],
    requiredContext: [],
    legacyEndpoint: "",
  });

  const categoryOptions = [
    { key: DocumentCategory.ACQUISITION, label: "Acquisitie" },
    { key: DocumentCategory.CONTRACT, label: "Contract" },
    { key: DocumentCategory.INSPECTION, label: "Inspectie" },
    { key: DocumentCategory.LEGAL, label: "Juridisch" },
    { key: DocumentCategory.RENOVATION, label: "Renovatie" },
    { key: DocumentCategory.GENERAL, label: "Algemeen" },
  ];

  const contextOptions = [
    { key: RequiredContext.UNIT_ID, label: "Unit ID" },
    { key: RequiredContext.CONTRACT_ID, label: "Contract ID" },
    { key: RequiredContext.ACQUISITION_ID, label: "Acquisitie ID" },
    { key: RequiredContext.BUILDING_ID, label: "Gebouw ID" },
    { key: RequiredContext.CONTACT_ID, label: "Contact ID" },
    { key: RequiredContext.COMPANY_ID, label: "Bedrijf ID" },
  ];

  const handleInputChange = (
    field: keyof CreateDocumentTemplateDto,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".docx")) {
        toast.error("Alleen .docx bestanden zijn toegestaan");
        return;
      }
      setSelectedFile(file);
      setAnalysisResult(null);
      setPreviewAnalysis(null);
    }
  };

  const handlePreviewAnalysis = async () => {
    if (!selectedFile) {
      toast.error("Selecteer eerst een bestand");
      return;
    }

    setIsLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", selectedFile);

      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates/analyze`,
        {
          method: "POST",
          body: formDataUpload,
        },
        session,
      );

      if (response.ok) {
        const result = await response.json();
        setPreviewAnalysis(result.analysis);
        setEntityInfo(result.entityInfo);
        toast.success("Bestand geanalyseerd!");
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij het analyseren van het bestand");
      }
    } catch (error) {
      console.error("Error analyzing file:", error);
      toast.error("Fout bij het analyseren van het bestand");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !formData.name) {
      toast.error("Selecteer een bestand en vul een naam in");
      return;
    }

    setIsLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", selectedFile);
      formDataUpload.append("templateName", formData.name);

      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates/upload`,
        {
          method: "POST",
          body: formDataUpload,
        },
        session,
      );

      if (response.ok) {
        const result = await response.json();
        setAnalysisResult(result);
        setEntityInfo(result.entityInfo);

        // Update form data with SharePoint file ID and extracted placeholders
        setFormData((prev) => ({
          ...prev,
          sharePointTemplateId: result.fileId,
          availablePlaceholders: result.analysis.validPlaceholders.map(
            (p: ExtractedPlaceholder) => p.placeholder,
          ),
        }));

        setCurrentStep(3);
        toast.success("Bestand succesvol geüpload en geanalyseerd");
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij het uploaden van het bestand");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Fout bij het uploaden van het bestand");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContextChange = (keys: any) => {
    const selectedKeys = Array.from(keys) as RequiredContext[];
    setFormData((prev) => ({
      ...prev,
      requiredContext: selectedKeys,
    }));
  };

  const handleSubmit = async () => {
    if (
      !formData.templateKey ||
      !formData.name ||
      !formData.sharePointTemplateId
    ) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
        session,
      );

      if (response.ok) {
        toast.success("Document sjabloon succesvol aangemaakt");
        onSuccess();
        onClose();
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij het aanmaken van het sjabloon");
      }
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("Fout bij het aanmaken van het sjabloon");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      templateKey: "",
      name: "",
      category: DocumentCategory.GENERAL,
      sharePointTemplateId: "",
      description: "",
      availablePlaceholders: [],
      requiredContext: [],
      legacyEndpoint: "",
    });
    setSelectedFile(null);
    setAnalysisResult(null);
    setPreviewAnalysis(null);
    setCurrentStep(1);
    setEntityInfo({});
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const renderPlaceholderAnalysis = (analysis: DocxAnalysisResult) => {
    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-green-200 bg-green-50">
            <CardBody className="p-4 text-center">
              <p className="text-2xl font-bold text-green-700">
                {analysis.validPlaceholders.length}
              </p>
              <p className="text-sm text-green-600">Geldige placeholders</p>
            </CardBody>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardBody className="p-4 text-center">
              <p className="text-2xl font-bold text-red-700">
                {analysis.invalidPlaceholders.length}
              </p>
              <p className="text-sm text-red-600">Ongeldige placeholders</p>
            </CardBody>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardBody className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">
                {Object.keys(analysis.entityMap).length}
              </p>
              <p className="text-sm text-blue-600">Entiteiten gevonden</p>
            </CardBody>
          </Card>
          <Card className="border-purple-200 bg-purple-50">
            <CardBody className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">
                {analysis.dynamicValuePlaceholders?.length || 0}
              </p>
              <p className="text-sm text-purple-600">Dynamische waarden</p>
            </CardBody>
          </Card>
        </div>

        {/* Valid Placeholders by Entity */}
        {Object.keys(analysis.entityMap).length > 0 && (
          <Card>
            <CardBody>
              <h4 className="mb-4 text-lg font-semibold">
                Gevonden Entiteiten & Properties
              </h4>
              <Accordion variant="bordered">
                {Object.entries(analysis.entityMap).map(
                  ([entity, properties]) => (
                    <AccordionItem
                      key={entity}
                      title={
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {entityInfo[entity]?.name || entity}
                          </span>
                          <Chip size="sm" color="primary" variant="flat">
                            {properties.length} properties
                          </Chip>
                        </div>
                      }
                    >
                      <div className="space-y-2">
                        <p className="mb-3 text-sm text-gray-600">
                          {entityInfo[entity]?.description ||
                            `Properties voor ${entity}`}
                        </p>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                          {properties.map((property) => {
                            const entityProp = entityInfo[
                              entity
                            ]?.properties.find((p) => p.key === property);
                            return (
                              <div
                                key={property}
                                className="flex items-center justify-between rounded border p-2"
                              >
                                <div>
                                  <code className="font-mono text-sm text-blue-600">
                                    {`{${entity}.${property}}`}
                                  </code>
                                  {entityProp && (
                                    <p className="text-xs text-gray-500">
                                      {entityProp.description}
                                    </p>
                                  )}
                                </div>
                                {entityProp && (
                                  <span className="text-xs text-gray-400">
                                    bijv: {entityProp.example}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </AccordionItem>
                  ),
                )}
              </Accordion>
            </CardBody>
          </Card>
        )}

        {/* Dynamic Value Placeholders */}
        {analysis.dynamicValuePlaceholders &&
          analysis.dynamicValuePlaceholders.length > 0 && (
            <Card className="border-purple-200 bg-purple-50">
              <CardBody>
                <h4 className="mb-4 text-lg font-semibold text-purple-700">
                  Dynamische Waarden Gevonden
                </h4>
                <p className="mb-4 text-sm text-purple-600">
                  Deze placeholders kunnen tijdens document generatie worden
                  ingevuld met aangepaste waarden:
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {analysis.dynamicValuePlaceholders.map(
                    (placeholder, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 rounded-lg border border-purple-200 bg-white p-3"
                      >
                        <MagnifyingGlass
                          size={16}
                          className="flex-shrink-0 text-purple-500"
                        />
                        <div className="flex-1">
                          <code className="rounded bg-purple-100 px-2 py-1 font-mono text-sm text-purple-700">
                            {`{${placeholder.placeholder}}`}
                          </code>
                          <p className="mt-1 text-xs text-purple-600">
                            Dynamische waarde:{" "}
                            <strong>{placeholder.dynamicValueKey}</strong>
                          </p>
                        </div>
                        <Chip size="sm" color="secondary" variant="flat">
                          Dynamisch
                        </Chip>
                      </div>
                    ),
                  )}
                </div>
                <div className="mt-4 rounded-lg bg-purple-100 p-3">
                  <p className="text-xs text-purple-700">
                    <strong>💡 Tip:</strong> Dynamische waarden worden tijdens
                    document generatie ingevuld via tekstinvoervelden. Deze
                    placeholders zijn altijd geldig en hoeven niet in de
                    database te bestaan.
                  </p>
                </div>
              </CardBody>
            </Card>
          )}

        {/* Invalid Placeholders */}
        {analysis.invalidPlaceholders.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardBody>
              <h4 className="mb-4 text-lg font-semibold text-red-700">
                Ongeldige Placeholders
              </h4>
              <div className="space-y-2">
                {analysis.invalidPlaceholders.map((placeholder, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 rounded border border-red-200 bg-white p-2"
                  >
                    <XCircle
                      size={16}
                      className="mt-0.5 flex-shrink-0 text-red-500"
                    />
                    <div className="flex-1">
                      <code className="font-mono text-sm text-red-600">{`{${placeholder.placeholder}}`}</code>
                      {placeholder.errorMessage && (
                        <p className="mt-1 text-xs text-red-500">
                          {placeholder.errorMessage}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Stap 1: Basis Informatie</h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="Sjabloon Key"
                placeholder="bijv. rental_contract_residential"
                value={formData.templateKey}
                onChange={(e) =>
                  handleInputChange("templateKey", e.target.value)
                }
                isRequired
                description="Unieke identifier voor het sjabloon"
              />
              <Input
                label="Naam"
                placeholder="bijv. Huurcontract Residentieel"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                isRequired
              />
            </div>

            <Select
              label="Categorie"
              selectedKeys={[formData.category]}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0] as DocumentCategory;
                handleInputChange("category", key);
              }}
              isRequired
            >
              {categoryOptions.map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>

            <Textarea
              label="Beschrijving"
              placeholder="Beschrijf waarvoor dit sjabloon wordt gebruikt..."
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              Stap 2: Word Document Analyseren
            </h3>

            <Card className="border-2 border-dashed border-gray-300 p-6">
              <CardBody className="text-center">
                <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="mb-2 text-lg font-medium">
                  Upload je Word document
                </p>
                <p className="mb-4 text-sm text-gray-600">
                  Gebruik placeholders zoals{" "}
                  <code className="rounded bg-gray-100 px-1">{`{contact.firstName}`}</code>{" "}
                  in je document
                </p>

                <input
                  type="file"
                  accept=".docx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                >
                  <Upload size={16} />
                  Bestand Selecteren
                </label>

                {selectedFile && (
                  <div className="mt-4 rounded-lg bg-gray-50 p-3">
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-gray-600">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>

            {selectedFile && (
              <div className="flex gap-2">
                <Button
                  color="secondary"
                  variant="bordered"
                  onPress={handlePreviewAnalysis}
                  isLoading={isLoading}
                  startContent={<Eye size={16} />}
                >
                  Preview Analyse
                </Button>
                <Button
                  color="primary"
                  onPress={handleFileUpload}
                  isLoading={isLoading}
                  className="flex-1"
                >
                  Upload & Analyseer
                </Button>
              </div>
            )}

            {/* Preview Analysis */}
            {previewAnalysis && (
              <div>
                <h4 className="text-md mb-3 font-semibold">Preview Analyse</h4>
                {renderPlaceholderAnalysis(previewAnalysis)}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              Stap 3: Resultaat & Configuratie
            </h3>

            {analysisResult && (
              <>
                <Card className="border-green-200 bg-green-50">
                  <CardBody>
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle size={20} />
                      <span className="font-medium">
                        Bestand succesvol geüpload en geanalyseerd!
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-green-600">
                      {analysisResult.fileName}
                    </p>
                  </CardBody>
                </Card>

                {renderPlaceholderAnalysis(analysisResult.analysis)}
              </>
            )}

            <Select
              label="Vereiste Context"
              selectionMode="multiple"
              selectedKeys={formData.requiredContext}
              onSelectionChange={handleContextChange}
              description="Welke gegevens zijn nodig om dit document te genereren?"
            >
              {contextOptions.map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex items-center gap-2">
            <Plus size={20} />
            Nieuw Document Sjabloon (Entity-Based)
          </div>
        </ModalHeader>
        <ModalBody>
          {/* Progress indicator */}
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">
                Stap {currentStep} van 3
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((currentStep / 3) * 100)}%
              </span>
            </div>
            <Progress value={(currentStep / 3) * 100} className="w-full" />
          </div>

          {renderStepContent()}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose}>
            Annuleren
          </Button>

          {currentStep > 1 && (
            <Button
              variant="bordered"
              onPress={() => setCurrentStep(currentStep - 1)}
            >
              Vorige
            </Button>
          )}

          {currentStep < 3 ? (
            <Button
              color="primary"
              onPress={() => setCurrentStep(currentStep + 1)}
              isDisabled={
                (currentStep === 1 &&
                  (!formData.name || !formData.templateKey)) ||
                (currentStep === 2 && !analysisResult)
              }
            >
              Volgende
            </Button>
          ) : (
            <Button
              color="primary"
              onPress={handleSubmit}
              isLoading={isLoading}
            >
              Sjabloon Aanmaken
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateDocumentTemplateModalEntityBased;
